import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@clerk/react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { motion } from "motion/react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { createProjectsApi, type ProjectListItem } from "@/lib/api/projects"
import { createOnboardingApi } from "@/lib/api/onboarding"
import { cn } from "@/lib/utils"
import { TemplatePreviewDialog } from "./TemplatePreviewDialog"

const GRADIENTS = [
  "from-violet-600 to-indigo-600",
  "from-pink-600 to-rose-600",
  "from-amber-600 to-orange-600",
  "from-emerald-600 to-teal-600",
  "from-blue-600 to-cyan-600",
  "from-fuchsia-600 to-purple-600",
]

// Maps content goals to template names they relate to.
// Mirrors backend GOAL_TEMPLATE_SCORES — kept lightweight for sorting only.
const GOAL_TEMPLATE_NAMES: Record<string, string[]> = {
  short_form_video: ["YouTube Short"],
  long_form_video: ["Tutorial Explainer"],
  images_visuals: ["Product Showcase", "Social Media Ad"],
  audio_podcasts: ["Podcast Clip"],
  written_content: ["Tutorial Explainer", "YouTube Short"],
  ad_creatives: ["Social Media Ad", "Product Showcase"],
  ai_agents: ["YouTube Short", "Tutorial Explainer"],
  repurpose_content: ["YouTube Short", "Tutorial Explainer"],
}

function getRecommendedNames(goals: string[] | null | undefined): Set<string> {
  if (!goals?.length) return new Set()
  const names = new Set<string>()
  for (const goal of goals) {
    for (const name of GOAL_TEMPLATE_NAMES[goal] ?? []) {
      names.add(name)
    }
  }
  return names
}

export function TemplatesGallery() {
  const { getToken } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const projectsApi = createProjectsApi({ getToken })
  const onboardingApi = createOnboardingApi({ getToken })

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)

  const { data: templates = [] } = useQuery({
    queryKey: ["templates"],
    queryFn: () => projectsApi.listTemplates(),
  })

  const { data: onboardingProfile } = useQuery({
    queryKey: ["onboarding-profile"],
    queryFn: () => onboardingApi.getProfile(),
    staleTime: Infinity,
  })

  const recommendedNames = useMemo(
    () => getRecommendedNames(onboardingProfile?.content_goals),
    [onboardingProfile?.content_goals],
  )

  // Sort recommended templates to the top
  const sortedTemplates = useMemo(() => {
    if (recommendedNames.size === 0) return templates
    return [...templates].sort((a, b) => {
      const aRec = recommendedNames.has(a.name) ? 0 : 1
      const bRec = recommendedNames.has(b.name) ? 0 : 1
      return aRec - bRec
    })
  }, [templates, recommendedNames])

  // Fetch full template (with graph) when one is selected
  const { data: selectedTemplate, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ["project", selectedTemplateId],
    queryFn: () => projectsApi.get(selectedTemplateId!),
    enabled: selectedTemplateId !== null,
  })

  const { data: recentData } = useQuery({
    queryKey: ["projects", "gallery-recent"],
    queryFn: () => projectsApi.list({ sortBy: "last_viewed_at", sortOrder: "desc", limit: 6 }),
  })

  const { data: allProjectsData } = useQuery({
    queryKey: ["projects", "gallery-all"],
    queryFn: () => projectsApi.list({ sortBy: "updated_at", sortOrder: "desc", limit: 50 }),
  })

  const createFromTemplate = useMutation({
    mutationFn: (templateId: string) => projectsApi.createFromTemplate(templateId),
    onSuccess: (project) => {
      setSelectedTemplateId(null)
      queryClient.setQueryData(["project", String(project.id)], project)
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      navigate(`/projects/${project.id}`)
    },
  })

  const recent = recentData?.items ?? []
  const projects = allProjectsData?.items ?? []

  return (
    <div data-tour="templates" className="relative z-10 w-full mb-8">
      <div className="bg-background rounded-2xl px-6 pb-8 pt-6">
        <div className="mx-auto max-w-4xl">
          <Tabs defaultValue="templates">
            <div className="mb-6 flex items-center justify-between">
              <TabsList variant="line">
                <TabsTrigger value="recent">Recently viewed</TabsTrigger>
                <TabsTrigger value="projects">My projects</TabsTrigger>
                <TabsTrigger value="templates">Templates</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="recent">
              <div className="grid gap-6 sm:grid-cols-2">
                {recent.map((p, i) => (
                  <TemplateCard
                    key={p.id}
                    title={p.name}
                    subtitle={p.description ?? ""}
                    gradient={GRADIENTS[i % GRADIENTS.length]}
                    index={i}
                    onClick={() => navigate(`/projects/${p.id}`)}
                  />
                ))}
                {recent.length === 0 && <EmptyState message="No recent projects." />}
              </div>
            </TabsContent>

            <TabsContent value="projects">
              <div className="grid gap-6 sm:grid-cols-2">
                {projects.map((p, i) => (
                  <TemplateCard
                    key={p.id}
                    title={p.name}
                    subtitle={p.description ?? ""}
                    gradient={GRADIENTS[i % GRADIENTS.length]}
                    index={i}
                    onClick={() => navigate(`/projects/${p.id}`)}
                  />
                ))}
                {projects.length === 0 && (
                  <EmptyState message="No projects yet. Start by creating one above." />
                )}
              </div>
            </TabsContent>

            <TabsContent value="templates">
              <div className="grid gap-6 sm:grid-cols-2">
                {sortedTemplates.map((t, i) => (
                  <TemplateCard
                    key={t.id}
                    title={t.name}
                    subtitle={t.description ?? ""}
                    gradient={GRADIENTS[i % GRADIENTS.length]}
                    index={i}
                    recommended={recommendedNames.has(t.name)}
                    onClick={() => setSelectedTemplateId(t.id)}
                  />
                ))}
                {sortedTemplates.length === 0 && <EmptyState message="No templates available." />}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <TemplatePreviewDialog
        template={selectedTemplate ?? null}
        open={selectedTemplateId !== null}
        onOpenChange={(open) => { if (!open) setSelectedTemplateId(null) }}
        onUseTemplate={(id) => createFromTemplate.mutate(id)}
        loading={createFromTemplate.isPending || isLoadingTemplate}
      />
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-muted-foreground col-span-2 flex min-h-32 items-center justify-center text-sm">
      {message}
    </div>
  )
}

function TemplateCard({
  title,
  subtitle,
  gradient,
  index,
  onClick,
  loading = false,
  recommended = false,
}: {
  title: string
  subtitle: string
  gradient: string
  index: number
  onClick: () => void
  loading?: boolean
  recommended?: boolean
}) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: 0.1 * index + 0.15, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col text-left cursor-pointer disabled:opacity-60"
      onClick={onClick}
      disabled={loading}
    >
      <div className="relative">
        <div
          className={cn(
            "h-48 w-full rounded-xl bg-gradient-to-br opacity-85 transition-opacity group-hover:opacity-100",
            gradient,
          )}
        />
        {recommended && (
          <span className="absolute top-3 right-3 rounded-full bg-white/90 px-2.5 py-0.5 text-xs font-medium text-black dark:bg-black/70 dark:text-white">
            Recommended
          </span>
        )}
      </div>
      <div className="mt-3 px-1">
        <h3 className="text-foreground text-sm font-medium">{title}</h3>
        <p className="text-muted-foreground mt-0.5 text-xs">{subtitle}</p>
      </div>
    </motion.button>
  )
}

// Re-export for type use in parent components
export type { ProjectListItem }
